// ============================================================================
// Drama API — algo-api service, via algoRequest
// ============================================================================

import { algoRequest } from "~/api/algo-client";

/** Optional everywhere but the identity: the gateway omits fields it has no value for, and a
 *  required field it drops would fail the whole card rather than the one line it belongs to. */
export interface DramaSeries {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  tags?: string[];
  rate?: number;
  viewCount?: number;
  episodeCount?: number;
  badge?: string;
  previewVideoUrl?: string;
  likes?: number;
  isLiked?: boolean;
}

export interface DramaEpisode {
  episodeId: string;
  /** HLS. The backend may leave it off a locked episode or send it anyway, so the lock is also
   *  enforced by the client (see `withMembership`). */
  videoUrl?: string;
  /** Seconds. */
  duration?: number;
  /** Behind the membership, as decided for the viewer who asked. Read it through `withMembership`,
   *  which opens it to a member. */
  isLocked: boolean;
  feedItemId?: string;
  viewCount?: number;
  likes?: number;
  isLiked?: boolean;
  isWatched?: boolean;
  /** 0–1 fraction of the episode watched — the same unit `drama_watch_history` returns and
   *  `record_progress` accepts. */
  watchProgress?: number;
}

export interface DramaSeriesListResponse {
  series: DramaSeries[];
}

export interface DramaEpisodesResponse {
  episodes: DramaEpisode[];
}

/** One per series: the episode watched last. */
export interface DramaWatchHistoryItem {
  series: DramaSeries;
  episodeId: string;
  episodeNumber?: number;
  /** 0–1 fraction of that episode. */
  watchProgress?: number;
  /** Unix seconds. */
  lastWatchedAt?: number;
}

export interface DramaWatchHistoryResponse {
  items: DramaWatchHistoryItem[];
}

export interface DramaCategory {
  id: string;
  name: string;
}

export interface DramaDiscoverySection {
  key: string;
  title?: string;
  items?: DramaSeries[];
  categories?: DramaCategory[];
}

export interface DramaDiscoveryResponse {
  banners?: DramaSeries[];
  sections?: DramaDiscoverySection[];
}

export async function fetchDramaSeriesList(readToken: () => string | null): Promise<DramaSeriesListResponse> {
  return algoRequest<DramaSeriesListResponse>("/api/watch/drama_series_list", {}, readToken);
}

export async function fetchDramaEpisodes(
  seriesId: string,
  readToken: () => string | null
): Promise<DramaEpisodesResponse> {
  return algoRequest<DramaEpisodesResponse>("/api/watch/drama_episodes", { seriesId }, readToken);
}

export async function fetchDramaDiscovery(readToken: () => string | null): Promise<DramaDiscoveryResponse> {
  return algoRequest<DramaDiscoveryResponse>("/api/watch/drama_discovery", {}, readToken);
}

export async function fetchDramaWatchHistory(readToken: () => string | null): Promise<DramaWatchHistoryResponse> {
  return algoRequest<DramaWatchHistoryResponse>("/api/watch/drama_watch_history", {}, readToken);
}

/** Resolves once recorded. The caller only catches: `algoRequest` already reports failures. */
export async function recordDramaProgress(
  feedItemId: string,
  watchProgress: number,
  readToken: () => string | null
): Promise<void> {
  await algoRequest("/api/watch/record_progress", { feedItemId, watchProgress }, readToken);
}

/**
 * The episodes as this viewer may watch them: a member has every episode open that came with a URL.
 *
 * One without a URL stays locked even for a member. Opened, it would have nothing to play, and read
 * as a broken episode rather than a locked one.
 */
export function withMembership(episodes: DramaEpisode[], member: boolean): DramaEpisode[] {
  if (!member) return episodes;
  return episodes.map(episode => (episode.videoUrl ? { ...episode, isLocked: false } : episode));
}
